/**
 * The open-task strip under today (SPEC §3.1, §5.5): the query
 * `#task status:open (assignee:@me or assignee:none) sort:due`, kept live from the task index.
 * Seeing open tasks costs 0 actions (SPEC §3.3); the list folds away and remembers that.
 */
import { useMemo, useState, useSyncExternalStore } from 'react'
import { OPEN_TASKS_QUERY, parseQuery, runQuery, TAG, type Node } from '@solar/core'
import { todayKey } from './dates.ts'
import { DueChip, shortDate } from './fields.tsx'
import { useStore } from './hooks.ts'
import { Content } from './render.tsx'
import { navigate } from './router.ts'

const SHOW = 8
const OPEN_KEY = 'solar-strip-open'
const QUERY = parseQuery(OPEN_TASKS_QUERY)

/** Where a task lives: nothing for today (it is right below), a short date, or the page title. */
function where(store: ReturnType<typeof useStore>, n: Node): string {
  let cur = n
  while (cur.parentId) cur = store.g.get(cur.parentId) ?? cur
  if (cur === n) return ''
  if (cur.kind === 'journal' && cur.date) return cur.date === todayKey() ? '' : shortDate(cur.date)
  return cur.title ?? ''
}

export function TaskStrip() {
  const store = useStore()
  const tv = useSyncExternalStore(store.tasks.subscribe, store.tasks.getVersion, store.tasks.getVersion)
  const nv = useSyncExternalStore(store.titles.subscribe, store.titles.getVersion, store.titles.getVersion)
  const [open, setOpen] = useState(() => localStorage.getItem(OPEN_KEY) !== '0')
  const tasks = useMemo(
    () => runQuery({ ...store.tagEnv, today: todayKey(), me: store.me, candidates: () => store.tasks.all() }, QUERY),
    [store, tv, nv], // eslint-disable-line react-hooks/exhaustive-deps
  )
  if (tasks.length === 0) return <div className="strip" />
  const toggle = (): void => {
    localStorage.setItem(OPEN_KEY, open ? '0' : '1')
    setOpen(!open)
  }
  return (
    <div className="strip">
      <button className="link strip-head" aria-expanded={open} onClick={toggle}>
        <span className="caret">{open ? '▾' : '▸'}</span> {tasks.length} open task{tasks.length === 1 ? '' : 's'}
      </button>
      {open && (
        <div className="strip-list">
          {tasks.slice(0, SHOW).map((n) => (
            <div key={n.id} className="strip-task" data-id={n.id}>
              <span className="strip-text" onClick={() => navigate(`/n/${n.id}`)}>
                <Content content={n.content} nodeId={n.id} />
              </span>
              <DueChip node={n} />
              <span className="strip-where">{where(store, n)}</span>
            </div>
          ))}
          {tasks.length > SHOW && (
            <a className="strip-more" href={`/n/${TAG.task}`} onClick={(e) => (e.preventDefault(), navigate(`/n/${TAG.task}`))}>
              All {tasks.length} open tasks
            </a>
          )}
        </div>
      )}
    </div>
  )
}
